/**
 * The fetch wrapper every request goes through.
 *
 * Three jobs, and nothing else:
 *
 * 1. Send the session cookie (`credentials: 'include'`) and the CSRF header on every
 *    state-changing request. The backend rejects a write without it, so doing this in
 *    one place means no caller can forget.
 * 2. Normalise every failure into one `ApiError`. The API guarantees a single error
 *    shape; this makes the client side match, so a screen never has to guess whether
 *    it has a network failure, an HTML error page or a real API error.
 * 3. Surface the request id, so an error a user reports can be found in the logs.
 */

import { apiUrl } from '@/lib/env';

export const CSRF_COOKIE = 'flyst_csrf';
export const CSRF_HEADER = 'X-CSRF-Token';
export const REQUEST_ID_HEADER = 'X-Request-ID';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** The shape the API returns for every failure, without exception. */
export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    details: Record<string, unknown>;
  };
};

/** One field-level message from a validation failure. */
export type FieldError = {
  field: string;
  message: string;
  type: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string | null;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? {};
    this.requestId = init.requestId ?? null;
  }

  /** Not signed in, or the session expired. The router sends these back to login. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isValidation(): boolean {
    return this.status === 422;
  }

  /**
   * Field-level messages, when the failure was a validation error.
   *
   * Empty for every other kind, so a form can map these onto its inputs without
   * checking the status first.
   */
  get fieldErrors(): FieldError[] {
    const fields = this.details.fields;
    if (!Array.isArray(fields)) return [];
    return fields.filter(
      (entry): entry is FieldError =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as FieldError).field === 'string' &&
        typeof (entry as FieldError).message === 'string',
    );
  }
}

/** Read a cookie by name. Only ever used for the CSRF token, which is not HttpOnly. */
export function readCookie(name: string): string | null {
  const pattern = new RegExp(
    `(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`,
  );
  const match = pattern.exec(document.cookie);
  const value = match?.[1];
  return value ? decodeURIComponent(value) : null;
}

export type RequestOptions = {
  method?: string;
  anonymous?: boolean;
  /** Serialised as JSON. Use `formData` for uploads. */
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = apiUrl(path);
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // An absent filter and an empty one mean the same thing, and neither belongs in
    // the query string.
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `${url}?${serialised}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get(REQUEST_ID_HEADER);

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // A proxy timing out, a gateway error page, an empty 502, none of which is JSON.
    return new ApiError({
      status: response.status,
      code: 'UNEXPECTED_RESPONSE',
      message: 'The server returned something unexpected. Try again.',
      requestId,
    });
  }

  const error = (body as Partial<ApiErrorBody>).error;
  if (!error || typeof error.code !== 'string') {
    return new ApiError({
      status: response.status,
      code: 'UNEXPECTED_RESPONSE',
      message: 'The server returned something unexpected. Try again.',
      requestId,
    });
  }

  return new ApiError({
    status: response.status,
    code: error.code,
    message: error.message ? error.message : 'Something went wrong.',
    details: error.details,
    requestId,
  });
}

/**
 * Make a request.
 *
 * Throws `ApiError` on any non-2xx, so callers only handle the success path and
 * TanStack Query handles the rest.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { ...options.headers };

  if (!options.anonymous && !SAFE_METHODS.has(method)) {
    // Double-submit: the backend compares this against the cookie it set. A write
    // without it is rejected, so it belongs here rather than in every caller.
    const token = readCookie(CSRF_COOKIE);
    if (token) headers[CSRF_HEADER] = token;
  }

  let body: BodyInit | undefined;
  if (options.formData) {
    // Deliberately no Content-Type: the browser has to set it with the multipart
    // boundary, and setting it by hand produces a request the server cannot parse.
    body = options.formData;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      // The session is a cookie, so every request has to carry it.
      credentials: options.anonymous ? 'omit' : 'include',
      headers,
      ...(body !== undefined ? { body } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Could not reach the server. Check your connection and try again.',
    });
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Upload a file, reporting how far it has got.
 *
 * `XMLHttpRequest` rather than `fetch`, for one reason: fetch cannot report upload
 * progress. It has no equivalent of `upload.onprogress`, and streaming a request body
 * to fake one is not something to rely on. For a ten-megabyte passport scan on an
 * office connection that difference is the whole experience: a bar that moves, or a
 * spinner that might mean anything.
 *
 * Everything else matches `request`: the session cookie travels, the CSRF header is
 * attached from the same place, and every failure comes back as the same `ApiError`,
 * so a caller cannot tell the two transports apart except by the progress.
 */
export function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  options: {
    /** Query parameters. The file endpoints take their `kind` in the URL. */
    query?: RequestOptions['query'];
    onProgress?: (fraction: number) => void;
  } = {},
): Promise<T> {
  const { query, onProgress } = options;

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildUrl(path, query));
    xhr.withCredentials = true;

    const token = readCookie(CSRF_COOKIE);
    if (token) xhr.setRequestHeader(CSRF_HEADER, token);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        // Not every browser knows the total for every body. Reporting nothing is
        // better than reporting a number that is wrong.
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      });
    }

    const fail = (status: number, code: string, message: string) => {
      reject(
        new ApiError({
          status,
          code,
          message,
          requestId: xhr.getResponseHeader(REQUEST_ID_HEADER),
        }),
      );
    };

    xhr.addEventListener('load', () => {
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText) as unknown;
      } catch {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(undefined as T);
          return;
        }
        fail(xhr.status, 'UNEXPECTED_RESPONSE', 'The server returned something unexpected.');
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T);
        return;
      }

      const error = (body as Partial<ApiErrorBody>).error;
      if (!error || typeof error.code !== 'string') {
        fail(xhr.status, 'UNEXPECTED_RESPONSE', 'The server returned something unexpected.');
        return;
      }

      reject(
        new ApiError({
          status: xhr.status,
          code: error.code,
          message: error.message ? error.message : 'Something went wrong.',
          details: error.details,
          requestId: xhr.getResponseHeader(REQUEST_ID_HEADER),
        }),
      );
    });

    xhr.addEventListener('error', () => {
      fail(0, 'NETWORK_ERROR', 'Could not reach the server. Check your connection.');
    });

    xhr.addEventListener('abort', () => {
      fail(0, 'ABORTED', 'The upload was cancelled.');
    });

    // Deliberately no Content-Type: the browser sets it with the multipart boundary,
    // and setting it by hand produces a request the server cannot parse.
    xhr.send(formData);
  });
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'POST', formData }),
};
